/**
 * Droits RGPD côté client — wrappers des callables RGPD.
 *
 *  - exportUserData()   : art. 15 + 20 — récupère un JSON complet des
 *                         données personnelles de l'utilisateur et déclenche
 *                         un téléchargement blob.
 *  - deleteUserAccount(): art. 17 — supprime définitivement le compte après
 *                         ré-authentification. Gère le flow auth/requires-
 *                         recent-login en re-déclenchant une popup Google ou
 *                         en demandant le mot de passe.
 *
 * Les deux callables ont `enforceAppCheck: true` côté serveur, donc elles
 * ne marchent qu'avec un App Check token valide (ou un debug token enregistré
 * en dev).
 */

import {
  GoogleAuthProvider,
  reauthenticateWithPopup,
  reauthenticateWithCredential,
  EmailAuthProvider,
  signOut as fbSignOut,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "./firebase";

type ExportResponse =
  | { ok: true; generatedAt: string; data: Record<string, unknown> }
  | {
      ok: false;
      code: "rate-limited" | "unauthenticated";
      message: string;
      retryAfterSec?: number;
    };

type DeleteResponse =
  | { ok: true; deletedAt: string }
  | {
      ok: false;
      code: "requires-recent-login" | "unauthenticated";
      message: string;
    };

const callExport = httpsCallable<Record<string, never>, ExportResponse>(
  functions,
  "exportUserData",
);

const callDelete = httpsCallable<
  { confirmEmail: string },
  DeleteResponse
>(functions, "deleteUserAccount");

export class RateLimitError extends Error {
  retryAfterSec: number;
  constructor(message: string, retryAfterSec: number) {
    super(message);
    this.name = "RateLimitError";
    this.retryAfterSec = retryAfterSec;
  }
}

/** Déclenche l'export et un download blob `arpentia-data-export-{uid}.json`. */
export async function exportAndDownloadUserData(): Promise<void> {
  const { data } = await callExport({});
  if (!data.ok) {
    if (data.code === "rate-limited") {
      throw new RateLimitError(data.message, data.retryAfterSec ?? 0);
    }
    throw new Error(data.message);
  }

  const blob = new Blob([JSON.stringify(data.data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const stamp = new Date(data.generatedAt)
    .toISOString()
    .replace(/[:.]/g, "-")
    .slice(0, 19);
  const uid = (auth.currentUser?.uid ?? "user").slice(0, 8);
  a.href = url;
  a.download = `arpentia-data-export-${uid}-${stamp}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Force une ré-auth récente avant la suppression. Détecte le provider et
 * utilise le bon flow (popup Google OU mot de passe). Pour password auth,
 * `password` doit être passé par l'appelant.
 */
export async function reauthenticateCurrentUser(password?: string): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error("Aucun utilisateur connecté.");
  const provider = user.providerData[0]?.providerId;
  if (provider === "google.com") {
    const googleProvider = new GoogleAuthProvider();
    googleProvider.setCustomParameters({ prompt: "select_account" });
    await reauthenticateWithPopup(user, googleProvider);
    return;
  }
  if (provider === "password") {
    if (!password) throw new Error("Mot de passe requis pour confirmer.");
    if (!user.email) throw new Error("Compte sans email — impossible de ré-authentifier.");
    const cred = EmailAuthProvider.credential(user.email, password);
    await reauthenticateWithCredential(user, cred);
    return;
  }
  throw new Error(`Ré-authentification non supportée pour le provider ${provider}.`);
}

/**
 * Supprime définitivement le compte. L'appelant doit avoir appelé
 * reauthenticateCurrentUser() dans la foulée (< 5 min) sinon la callable
 * renvoie `requires-recent-login`. Sur succès, déconnecte localement.
 */
export async function deleteAccountDefinitively(
  confirmEmail: string,
): Promise<void> {
  const { data } = await callDelete({ confirmEmail });
  if (!data.ok) {
    if (data.code === "requires-recent-login") {
      const err = new Error(data.message) as Error & { code: string };
      err.code = "requires-recent-login";
      throw err;
    }
    throw new Error(data.message);
  }
  // Déconnexion locale — la session Auth est déjà invalide côté serveur.
  try {
    await fbSignOut(auth);
  } catch {
    // Déjà déconnecté côté serveur — ignorer.
  }
}
